import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import { PageHeader } from '@/app/DashboardLayout';
import { usePermissions } from '@/hooks/usePermissions';
import { useFiscalYearStartMonth } from '@/hooks/useOrgSettings';
import { financialYearOf, financialYearPeriod, currentMonth, type Period } from '@/lib/dates';
import { Button, PeriodPicker } from '@/features/common/ui';
import { useActiveOrg } from '@/stores/auth';
import { BasisToggle, describeBasis, type Basis } from '@/features/dashboard/shared';
import { BreakdownCard } from './BreakdownCard';
import { MonthsChart } from './MonthsChart';

/**
 * Reports: where the money came from and where it went, over a period the
 * person chooses.
 *
 * ── WHY A PAGE WHEN THE DASHBOARD DRAWS THE SAME CARDS ──
 * The Dashboard answers "how is this month", and its periods are fixed to
 * that question - this month, this financial year. The questions this page is
 * for are the ones with a date range in them: how did the first half go, what
 * did the quarter we are about to file look like, what changed between two
 * Septembers. Same cards, any window, both sides given equal room - and the
 * charts keep the hover and click contract they already have, so every row
 * still opens the list it is made of (docs/design/reporting-audit.md P5).
 *
 * It is also where the export pack will live (P6): a period, the figures for
 * it, and one button - which is a sentence that only makes sense on a page
 * whose whole subject is the period.
 *
 * ── THE FINANCIAL YEAR, NOT THE MONTH ──
 * It opens on the year so far, because a person who came here rather than to
 * the Dashboard is asking a question wider than a month. The chips move it
 * anywhere in one tap.
 */
/**
 * The pack, as a file the browser saves.
 *
 * ── WHY A FETCH AND NOT A LINK ──
 * The request has to carry `x-org-id` - a person with several books would
 * otherwise export whichever one the server guessed - and a plain `<a href>`
 * sends no headers. So it is fetched with the session, turned into a blob and
 * handed to a synthetic link, which is also how `ShareInvoiceSheet` gets the
 * invoice PDF.
 *
 * The filename comes from the server's `Content-Disposition`, so what lands in
 * Downloads is named the same way whoever opens the API directly would get.
 */
function ExportPack({ period, basis }: { period: Period; basis: Basis }) {
  const org = useActiveOrg();
  const [busy, setBusy] = useState<'pdf' | 'xlsx' | null>(null);
  const [failed, setFailed] = useState(false);

  const take = async (format: 'pdf' | 'xlsx') => {
    if (!org || busy) return;
    setBusy(format);
    setFailed(false);
    try {
      const query = new URLSearchParams({ from: period.from, to: period.to, basis, format });
      const res = await fetch(`/v1/reports/export?${query.toString()}`, {
        credentials: 'include',
        headers: { 'x-org-id': org.id },
      });
      if (!res.ok) throw new Error(String(res.status));
      const disposition = res.headers.get('content-disposition') ?? '';
      const named = /filename="([^"]+)"/.exec(disposition)?.[1];
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = named || `report.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Freed on the next tick: revoking synchronously races the download in Safari.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {failed ? (
        <span role="status" className="text-xs text-destructive">
          That did not download. Try again.
        </span>
      ) : null}
      <Button variant="outline" size="sm" onClick={() => take('pdf')} disabled={busy !== null} data-testid="export-pdf">
        {busy === 'pdf' ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Download className="size-4" aria-hidden />}
        PDF
      </Button>
      <Button variant="outline" size="sm" onClick={() => take('xlsx')} disabled={busy !== null} data-testid="export-xlsx">
        {busy === 'xlsx' ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Download className="size-4" aria-hidden />}
        Excel
      </Button>
    </div>
  );
}

export function ReportsPage() {
  const perms = usePermissions();
  const fyStartMonth = useFiscalYearStartMonth();
  const fy = useMemo(
    () => financialYearPeriod(financialYearOf(currentMonth().from, fyStartMonth).startYear, fyStartMonth),
    [fyStartMonth],
  );
  const [period, setPeriod] = useState<Period>(fy);
  const [basis, setBasis] = useState<Basis>('cash');

  /**
   * The API refuses a report to somebody who can only see their own records,
   * and the nav hides the link for them; the route still has to refuse rather
   * than render four cards of errors for a person who typed the address.
   */
  if (!perms.seesAll('report')) return <Navigate to="/" replace />;

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Where the money came from and where it went, over any period you choose."
        actions={<ExportPack period={period} basis={basis} />}
      />

      <div className="space-y-4 px-5 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="max-w-xl text-sm text-muted-foreground">{describeBasis(basis)}</p>
          <div className="flex flex-wrap items-center gap-3">
            <BasisToggle value={basis} onChange={setBasis} />
            <PeriodPicker value={period} onChange={setPeriod} id="reports-period" />
          </div>
        </div>

        {/* Both sides the same size: this page has no opinion about which
            question the person came with. Each card carries its own dimension
            switch - income by item, HSN/SAC, customer or category; spend by
            category, vendor or project - and remembers the choice. */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <BreakdownCard side="income" period={period} basis={basis} title="Where the money came from" />
          <BreakdownCard side="expense" period={period} basis={basis} title="Where it went" />
        </div>

        {/* Month by month, each side on its own axis. Both, not just spend:
            the Dashboard draws the expense one because that is the question a
            month asks, and the income one is the half a period report needs. */}
        <MonthsChart side="income" period={period} basis={basis} title="Money in, month by month" />
        <MonthsChart side="expense" period={period} basis={basis} title="Money out, month by month" />
      </div>
    </>
  );
}
